import type { BannedUser, MutedUser } from '@pin/schemas/rest';
import {
  catchError,
  NEVER,
  repeat,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  timeout,
  timer,
} from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { withIdToken } from '../firebase/auth';
import { isVisible$ } from '../utils/rxjs.dom';
import { HttpError, retryWithBackoff } from '../utils/rxjs.http';

export type { BannedUser, MutedUser };

const fetchList$ = <T>(path: string) =>
  withIdToken((token) =>
    fromFetch(`${import.meta.env.VITE_ADMIN_API_URL}${path}`, {
      headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
      selector: (r): Promise<T[]> => {
        if (!r.ok) {
          throw new HttpError(r);
        }
        return r.json();
      },
    }).pipe(timeout(6_000), retryWithBackoff(3))
  );

/** Polls the list at `path` while the page is visible, and when `refresh$` fires. */
const pollList = <T>(
  path: string,
  refresh$: Subject<void>,
  errorDelay: number,
  repeatDelay: number
) =>
  isVisible$.pipe(
    switchMap((visible) => {
      if (!visible) {
        return NEVER;
      }
      return refresh$.pipe(
        startWith(0),
        switchMap(() =>
          fetchList$<T>(path).pipe(
            catchError((_err: unknown, source) =>
              timer(errorDelay).pipe(switchMap(() => source))
            ),
            repeat({ delay: () => timer(repeatDelay) })
          )
        )
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

export const bansSubject$ = new Subject<void>();
export const pollBans = (errorDelay = 5_000, repeatDelay = 5_000) =>
  pollList<BannedUser>('/admin/bans', bansSubject$, errorDelay, repeatDelay);

export const mutesSubject$ = new Subject<void>();
export const pollMutes = (errorDelay = 5_000, repeatDelay = 5_000) =>
  pollList<MutedUser>('/admin/mutes', mutesSubject$, errorDelay, repeatDelay);
